import { Aggregates, Category, Operation } from '../api/types';

/** Локальная дата YYYY-MM-DD, локальный TZ. */
function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function shiftIso(iso: string, days: number): string {
  const d = parseIso(iso);
  if (!d) return iso;
  d.setDate(d.getDate() + days);
  return todayIso(d);
}

function parseIso(s: string): Date | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

interface HotStreakResult {
  category: string;
  icon: string;
  days: number;
}

/**
 * Стрик «дней без [горячей] категории». Возвращает самую длинную
 * текущую серию среди всех hot-категорий.
 *
 * Серия не уходит раньше первой операции в истории и `stats_since` —
 * свежепомеченная категория без трат не даёт незаслуженных «365 дней без».
 *
 * «Пол» истории берётся из `aggregates.first_op_date` — это MIN(date) по всей
 * таблице, тогда как `operations` — лишь окно (план 3a). Минимум по окну всё
 * равно учитывается: локальная оптимистичная запись может быть старше агрегата.
 * Агрегата нет (старый снапшот из IndexedDB) — считаем по одним операциям.
 */
export function computeHotCategoryStreak(
  operations: Operation[],
  categories: Category[],
  now: Date = new Date(),
  since: string | null = null,
  aggregates?: Pick<Aggregates, 'first_op_date'> | null,
): HotStreakResult | null {
  const hot = categories.filter((c) => c.is_hot && c.type === 'Расход');
  if (hot.length === 0) return null;

  let firstOpDate: string | null = aggregates?.first_op_date ?? null;
  for (const o of operations) {
    if (firstOpDate === null || o.date < firstOpDate) firstOpDate = o.date;
  }
  if (!firstOpDate) return null; // истории нет — стрику не от чего считаться
  const floor = since && since > firstOpDate ? since : firstOpDate;

  let best: HotStreakResult | null = null;

  for (const c of hot) {
    const dates = new Set(
      operations.filter((o) => o.category === c.name).map((o) => o.date),
    );
    let cursor = shiftIso(todayIso(now), -1);
    let days = 0;
    for (let i = 0; i < 365; i++) {
      if (cursor < floor || dates.has(cursor)) break;
      days++;
      cursor = shiftIso(cursor, -1);
    }
    if (!best || days > best.days) best = { category: c.name, icon: c.icon, days };
  }

  return best && best.days > 0 ? best : null;
}
